import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal, Nav } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetAllUsersQuery, useDeleteUserMutation, useToggleAdminStatusMutation, useToggleAdFreeStatusMutation, useCreateDraftBotLeagueMutation, useResetLeagueDraftMutation, useUpdateUserAdminRecordMutation } from '../slices/usersAdminApiSlice';
import { useFetchLeaguesQuery } from '../slices/leaguesApiSlice';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import { useDeleteTeamMutation } from '../slices/teamApiSlice';
import { useLazyRefreshUserProfileQuery } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import Icon from '../components/Icon.jsx';
import SeasonManagement from '../components/SeasonManagement';
import { RotateCcw, Trophy, Users } from 'lucide-react';
import { buildDraftRoomPath } from '../utils/draftRoomMode';

const ADMIN_TAB = {
  LEAGUES: 'leagues',
  USERS: 'users',
};

const normalizeTableValue = (value) => String(value || '').trim().toLowerCase();

const formatAdminDate = (value) => {
  if (!value) {
    return 'Unknown';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Unknown';
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getDateSortValue = (value) => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const getAdminTeams = (userData) => {
  if (Array.isArray(userData?.teams) && userData.teams.length > 0) {
    return userData.teams;
  }

  return userData?.team ? [userData.team] : [];
};

const getAdminLeagues = (userData) => {
  if (Array.isArray(userData?.leagues) && userData.leagues.length > 0) {
    return userData.leagues;
  }

  return userData?.league ? [userData.league] : [];
};

const getAdminTeamNames = (userData) => getAdminTeams(userData)
  .map((team) => team?.name || '')
  .filter(Boolean)
  .join(' ');

const getAdminLeagueNames = (userData) => getAdminLeagues(userData)
  .map((league) => league?.name || '')
  .filter(Boolean)
  .join(' ');

const explodeAdminUserRows = (userData) => {
  const teams = getAdminTeams(userData);
  const leagues = getAdminLeagues(userData);

  if (teams.length === 0) {
    return [{
      ...userData,
      rowKey: `${userData?.user?._id || 'unknown'}-no-team`,
    }];
  }

  return teams.map((team, index) => {
    const matchedLeague = team?.league?._id
      ? leagues.find((league) => String(league?._id) === String(team.league._id)) || team.league
      : null;

    return {
      ...userData,
      rowKey: `${userData?.user?._id || 'unknown'}-${team?._id || index}`,
      team: team || null,
      teams: team ? [team] : [],
      league: matchedLeague || userData?.league || null,
      leagues: matchedLeague ? [matchedLeague] : (userData?.league ? [userData.league] : []),
    };
  });
};

const getUserAdminSortValue = (userData, sortKey) => {
  switch (sortKey) {
    case 'createdAt':
      return getDateSortValue(userData?.user?.createdAt);
    case 'username':
      return normalizeTableValue(userData?.user?.username);
    case 'email':
      return normalizeTableValue(userData?.user?.email);
    case 'athlete':
      return normalizeTableValue(`${userData?.athlete?.firstName || ''} ${userData?.athlete?.lastName || ''}`);
    case 'team':
      return normalizeTableValue(getAdminTeamNames(userData));
    case 'league':
      return normalizeTableValue(getAdminLeagueNames(userData));
    case 'admin':
      return userData?.athlete?.isAdmin ? 1 : 0;
    case 'ads':
      return userData?.user?.adFree ? 1 : 0;
    default:
      return '';
  }
};

const AdminScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    expectedWeeklyDistance: '',
    teamName: '',
    teamDescription: '',
  });
  const [activeAdminTab, setActiveAdminTab] = useState(ADMIN_TAB.LEAGUES);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [hasRejectedAccess, setHasRejectedAccess] = useState(false);
  const [leagueToReset, setLeagueToReset] = useState(null);
  const [userFilters, setUserFilters] = useState({
    keyword: '',
    username: '',
    email: '',
    athlete: '',
    team: '',
    league: '',
    admin: 'all',
    ads: 'all',
  });
  const [userSort, setUserSort] = useState({ key: 'createdAt', direction: 'desc' });

  const requestedLeagueId = new URLSearchParams(location.search).get('leagueId') || '';

  const { data: currentAthlete, isLoading: isLoadingCurrentAthlete } = useFetchAthleteByUserIdQuery(userInfo?._id, {
    skip: !userInfo?._id,
  });
  const { data: scopedLeagueAthlete, isLoading: isLoadingScopedLeagueAthlete } = useFetchAthleteByUserIdQuery({
    userId: userInfo?._id,
    leagueId: requestedLeagueId,
  }, {
    skip: !userInfo?._id || !requestedLeagueId,
  });
  const { data: requestedLeague, isLoading: isLoadingRequestedLeague } = useFetchLeaguesQuery(requestedLeagueId, {
    skip: !requestedLeagueId,
  });

  const effectiveAthleteId = userInfo?.athleteId || scopedLeagueAthlete?._id || currentAthlete?._id;
  const isSiteAdmin = Boolean(currentAthlete?.isAdmin);
  const isRequestedLeagueAdmin = Boolean(
    requestedLeagueId
    && requestedLeague
    && effectiveAthleteId
    && String(requestedLeague?.admin?._id || requestedLeague?.admin) === String(effectiveAthleteId)
  );
  const canAccessAdminScreen = isSiteAdmin || isRequestedLeagueAdmin;
  const shouldLoadSiteAdminData = isSiteAdmin;

  const { data: users, isLoading, error, refetch } = useGetAllUsersQuery(undefined, {
    skip: !shouldLoadSiteAdminData,
  });
  const { data: leagues, isLoading: leaguesLoading } = useFetchLeaguesQuery(undefined, {
    skip: !shouldLoadSiteAdminData,
  });
  const [updateUserAdminRecord, { isLoading: isSavingRecord }] = useUpdateUserAdminRecordMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [deleteTeam, { isLoading: isDeletingTeam }] = useDeleteTeamMutation();
  const [toggleAdminStatus, { isLoading: isToggling }] = useToggleAdminStatusMutation();
  const [toggleAdFreeStatus, { isLoading: isTogglingAdFree }] = useToggleAdFreeStatusMutation();
  const [createDraftBotLeague, { isLoading: isCreatingDraftBotLeague }] = useCreateDraftBotLeagueMutation();
  const [resetLeagueDraft, { isLoading: isResettingLeagueDraft }] = useResetLeagueDraftMutation();
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    if (!userInfo || hasRejectedAccess) {
      return;
    }

    const accessChecksPending = isLoadingCurrentAthlete
      || (requestedLeagueId && (isLoadingRequestedLeague || isLoadingScopedLeagueAthlete));

    if (accessChecksPending) {
      return;
    }

    if (!canAccessAdminScreen) {
      setHasRejectedAccess(true);
      toast.error('Not authorized as admin');
      navigate('/');
    }
  }, [
    userInfo,
    hasRejectedAccess,
    isLoadingCurrentAthlete,
    requestedLeagueId,
    isLoadingRequestedLeague,
    isLoadingScopedLeagueAthlete,
    canAccessAdminScreen,
    navigate,
  ]);

  useEffect(() => {
    if (!shouldLoadSiteAdminData || !leagues?.length) {
      return;
    }

    if (requestedLeagueId) {
      const matchingLeague = leagues.find((league) => league._id === requestedLeagueId);
      if (matchingLeague && matchingLeague._id !== selectedLeagueId) {
        setSelectedLeagueId(matchingLeague._id);
        return;
      }
    }

    if (!selectedLeagueId) {
      setSelectedLeagueId(leagues[0]._id);
    }
  }, [leagues, requestedLeagueId, selectedLeagueId, shouldLoadSiteAdminData]);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleUserFilterChange = (event) => {
    const { name, value } = event.target;
    setUserFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleClearUserFilters = () => {
    setUserFilters({
      keyword: '',
      username: '',
      email: '',
      athlete: '',
      team: '',
      league: '',
      admin: 'all',
      ads: 'all',
    });
    setUserSort({ key: 'createdAt', direction: 'desc' });
  };

  const handleUserSort = (key) => {
    setUserSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (key) => {
    if (userSort.key !== key) {
      return '';
    }

    return userSort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleEditClick = (userData) => {
    setRecordToEdit(userData);
    setEditForm({
      username: userData.user?.username || '',
      email: userData.user?.email || '',
      firstName: userData.athlete?.firstName || '',
      lastName: userData.athlete?.lastName || '',
      expectedWeeklyDistance: userData.athlete?.expectedWeeklyDistance ?? '',
      teamName: userData.team?.name || '',
      teamDescription: userData.team?.description || '',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    if (isSavingRecord) {
      return;
    }

    setShowEditModal(false);
    setRecordToEdit(null);
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleOpenResetDraftModal = (league) => {
    setLeagueToReset(league);
  };

  const handleCloseResetDraftModal = () => {
    if (isResettingLeagueDraft) {
      return;
    }

    setLeagueToReset(null);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.user._id).unwrap();
        toast.success('User deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete user');
      }
    }
    handleCloseModal();
  };

  const handleSaveRecord = async () => {
    if (!recordToEdit?.user?._id) {
      return;
    }

    try {
      await updateUserAdminRecord({
        userId: recordToEdit.user._id,
        teamId: recordToEdit.team?._id,
        user: {
          username: editForm.username,
          email: editForm.email,
        },
        athlete: {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          expectedWeeklyDistance: editForm.expectedWeeklyDistance,
        },
        team: {
          name: editForm.teamName,
          description: editForm.teamDescription,
        },
      }).unwrap();

      toast.success(`Updated ${editForm.username || recordToEdit.user.username}`);
      handleCloseEditModal();
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update user records');
    }
  };

  const handleDeleteTeam = async (userData) => {
    if (!userData?.team?._id) {
      return;
    }

    if (!window.confirm(`Delete team record for ${userData.user.username}?`)) {
      return;
    }

    try {
      await deleteTeam(userData.team._id).unwrap();
      toast.success('Team record deleted successfully');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete team record');
    }
  };

  const adminRows = (users || []).flatMap((userData) => explodeAdminUserRows(userData));

  const filteredUsers = adminRows.filter((userData) => {
    const teamNames = getAdminTeamNames(userData);
    const leagueNames = getAdminLeagueNames(userData);
    const keyword = normalizeTableValue(userFilters.keyword);
    const keywordHaystack = normalizeTableValue([
      userData?.user?.username,
      userData?.user?.email,
      userData?.user?._id,
      userData?.athlete?._id,
      `${userData?.athlete?.firstName || ''} ${userData?.athlete?.lastName || ''}`,
      teamNames,
      userData?.team?._id,
      leagueNames,
    ].filter(Boolean).join(' '));
    const usernameMatches = normalizeTableValue(userData?.user?.username).includes(normalizeTableValue(userFilters.username));
    const emailMatches = normalizeTableValue(userData?.user?.email).includes(normalizeTableValue(userFilters.email));
    const athleteName = `${userData?.athlete?.firstName || ''} ${userData?.athlete?.lastName || ''}`.trim();
    const athleteMatches = normalizeTableValue(athleteName).includes(normalizeTableValue(userFilters.athlete));
    const teamMatches = normalizeTableValue(teamNames).includes(normalizeTableValue(userFilters.team));
    const leagueMatches = normalizeTableValue(leagueNames).includes(normalizeTableValue(userFilters.league));
    const keywordMatches = !keyword || keywordHaystack.includes(keyword);
    const adminMatches = userFilters.admin === 'all'
      || (userFilters.admin === 'admin' && userData?.athlete?.isAdmin)
      || (userFilters.admin === 'user' && !userData?.athlete?.isAdmin);
    const adsMatches = userFilters.ads === 'all'
      || (userFilters.ads === 'off' && userData?.user?.adFree)
      || (userFilters.ads === 'on' && !userData?.user?.adFree);

    return keywordMatches && usernameMatches && emailMatches && athleteMatches && teamMatches && leagueMatches && adminMatches && adsMatches;
  }).sort((left, right) => {
    const leftValue = getUserAdminSortValue(left, userSort.key);
    const rightValue = getUserAdminSortValue(right, userSort.key);

    if (typeof leftValue === 'number' || typeof rightValue === 'number') {
      const numericDiff = Number(leftValue || 0) - Number(rightValue || 0);
      return userSort.direction === 'asc' ? numericDiff : -numericDiff;
    }

    const comparison = String(leftValue).localeCompare(String(rightValue));
    return userSort.direction === 'asc' ? comparison : -comparison;
  });

  const handleToggleAdmin = async (userData) => {
    const userId = userData.user._id;
    const currentIsAdmin = !!userData.athlete?.isAdmin;
    setUpdatingId(userId);
    try {
      await toggleAdminStatus({ userId, isAdmin: !currentIsAdmin }).unwrap();
      toast.success(`Admin status ${!currentIsAdmin ? 'granted' : 'revoked'} for ${userData.user.username}`);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update admin status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleAdFree = async (userData) => {
    const userId = userData.user._id;
    const currentAdFree = !!userData.user?.adFree;
    setUpdatingId(userId);
    try {
      await toggleAdFreeStatus({ userId, adFree: !currentAdFree }).unwrap();

      if (userId === userInfo?._id) {
        const refreshedUserInfo = await refreshUserProfile().unwrap();
        dispatch(setCredentials({ userInfo: refreshedUserInfo }));
      }

      toast.success(`Ad-free access ${!currentAdFree ? 'enabled' : 'disabled'} for ${userData.user.username}`);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update ad-free status');
    } finally {
      setUpdatingId(null);
    }
  };

  const selectedLeague = shouldLoadSiteAdminData
    ? leagues?.find((league) => league._id === selectedLeagueId) || null
    : requestedLeague || null;

  const sortedLeagues = shouldLoadSiteAdminData
    ? [...(leagues || [])].sort((left, right) => getDateSortValue(right?.createdAt) - getDateSortValue(left?.createdAt))
    : [];

  const handleCreateDraftBotLeague = async () => {
    try {
      const result = await createDraftBotLeague().unwrap();
      toast.success('Draft bot test league created. Entering draft room...');
      navigate(buildDraftRoomPath(result.draftId, { chooseView: true }));
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to create draft bot test league');
    }
  };

  const handleConfirmResetDraft = async () => {
    if (!leagueToReset?._id) {
      return;
    }

    try {
      const result = await resetLeagueDraft(leagueToReset._id).unwrap();
      toast.success(result?.message || 'League draft reset successfully');
      setLeagueToReset(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reset league draft');
    }
  };

  const isAuthorizing = isLoadingCurrentAthlete
    || (requestedLeagueId && (isLoadingRequestedLeague || isLoadingScopedLeagueAthlete));

  if (isAuthorizing || (shouldLoadSiteAdminData && isLoading)) {
    return <Loader />;
  }

  if (shouldLoadSiteAdminData && error) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="alert alert-danger mt-5">
            {error?.data?.message || 'Failed to load admin data'}
          </div>
        </div>
      </div>
    );
  }

  if (!canAccessAdminScreen) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="alert alert-danger mt-5">
            Not authorized as admin
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <h1 className="text-primary mb-4">{shouldLoadSiteAdminData ? 'Administration Dashboard' : 'Season Setup'}</h1>

          {shouldLoadSiteAdminData && (
            <div className="d-flex gap-2 flex-wrap mb-4">
              <Button variant="outline-secondary" onClick={() => navigate('/admin/news')}>
                News Publishing
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/admin/errors')}>
                View Error Logs
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/admin/garmin')}>
                Garmin Diagnostics
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateDraftBotLeague}
                disabled={isCreatingDraftBotLeague}
              >
                {isCreatingDraftBotLeague ? 'Creating Draft Test...' : 'Create Draft Test League'}
              </Button>
            </div>
          )}

          {shouldLoadSiteAdminData && (
            <Nav
              variant="tabs"
              activeKey={activeAdminTab}
              onSelect={(eventKey) => eventKey && setActiveAdminTab(eventKey)}
              className="mb-4"
            >
              <Nav.Item>
                <Nav.Link eventKey={ADMIN_TAB.LEAGUES}>League Administration</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={ADMIN_TAB.USERS}>User Administration</Nav.Link>
              </Nav.Item>
            </Nav>
          )}

          {(!shouldLoadSiteAdminData || activeAdminTab === ADMIN_TAB.LEAGUES) && (
          <div className="modern-card mb-5">
            <div className="modern-card-header">
              <h2 className="modern-card-title"><Icon icon={Trophy} size={18} className="me-2 align-middle" /> League Administration</h2>
              <p className="modern-card-subtitle">
                {shouldLoadSiteAdminData
                  ? 'Manage every site league, season setup, and draft resets'
                  : 'Create upcoming seasons, end active ones, and review season history for this league'}
              </p>
            </div>

            {shouldLoadSiteAdminData && leaguesLoading ? (
              <div className="text-center py-4">
                <Loader />
              </div>
            ) : shouldLoadSiteAdminData && leagues && leagues.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>League Name</th>
                      <th>Created</th>
                      <th>Description</th>
                      <th>Teams</th>
                      <th>Athletes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeagues.map((league) => (
                      <tr key={league._id}>
                        <td>{league.name}</td>
                        <td>{formatAdminDate(league.createdAt)}</td>
                        <td>{league.description || 'No description'}</td>
                        <td>
                          <span className="badge bg-primary">
                            {league.teams ? league.teams.length : 0} teams
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {league.athletes ? league.athletes.length : 0} athletes
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigate(`/admin/assignments/${league._id}`)}
                            >
                              Manage Assignments
                            </Button>
                            <Button
                              variant={selectedLeagueId === league._id ? 'success' : 'outline-primary'}
                              size="sm"
                              onClick={() => setSelectedLeagueId(league._id)}
                            >
                              Manage Seasons
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleOpenResetDraftModal(league)}
                            >
                              <Icon icon={RotateCcw} size={14} className="me-1 align-middle" /> Reset Draft
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => navigate(`/league/${league._id}`)}
                            >
                              View League
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : shouldLoadSiteAdminData ? (
              <div className="text-center py-5">
                <p className="text-secondary">No leagues found</p>
                <Button variant="primary" onClick={() => navigate('/leagues')}>
                  Create League
                </Button>
              </div>
            ) : null}

            {selectedLeague && (
              <div className="p-4 border-top">
                <div className="mb-3">
                  <h3 className="text-primary mb-1">Season Tools: {selectedLeague.name}</h3>
                  <p className="text-secondary mb-0">Create upcoming seasons, end active ones, and review season history for this league.</p>
                </div>
                <SeasonManagement leagueId={selectedLeague._id} isAdmin />
              </div>
            )}
          </div>
          )}

          {shouldLoadSiteAdminData && activeAdminTab === ADMIN_TAB.USERS && (
            <div className="modern-card mb-5">
              <div className="modern-card-header">
                <h2 className="modern-card-title"><Icon icon={Users} size={18} className="me-2 align-middle" /> User Administration</h2>
                <p className="modern-card-subtitle">Manage all user accounts and associated data</p>
              </div>

              {users && users.length > 0 ? (
                <div className="table-responsive">
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <Form.Control
                      size="sm"
                      name="keyword"
                      value={userFilters.keyword}
                      onChange={handleUserFilterChange}
                      placeholder="Keyword search across username, email, athlete, team, league, and ids"
                      style={{ maxWidth: '420px' }}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={handleClearUserFilters}>
                      Clear Filters
                    </Button>
                  </div>
                  <table className="table table-sm table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Actions</th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('createdAt')}>
                            Created{getSortIndicator('createdAt')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('username')}>
                            Username{getSortIndicator('username')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('email')}>
                            Email{getSortIndicator('email')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('athlete')}>
                            Athlete{getSortIndicator('athlete')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('team')}>
                            Team{getSortIndicator('team')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('league')}>
                            League{getSortIndicator('league')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('admin')}>
                            Admin{getSortIndicator('admin')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => handleUserSort('ads')}>
                            Ads{getSortIndicator('ads')}
                          </button>
                        </th>
                      </tr>
                      <tr>
                        <th />
                        <th />
                        <th><Form.Control size="sm" name="username" value={userFilters.username} onChange={handleUserFilterChange} placeholder="Filter" /></th>
                        <th><Form.Control size="sm" name="email" value={userFilters.email} onChange={handleUserFilterChange} placeholder="Filter" /></th>
                        <th><Form.Control size="sm" name="athlete" value={userFilters.athlete} onChange={handleUserFilterChange} placeholder="Filter" /></th>
                        <th><Form.Control size="sm" name="team" value={userFilters.team} onChange={handleUserFilterChange} placeholder="Filter" /></th>
                        <th><Form.Control size="sm" name="league" value={userFilters.league} onChange={handleUserFilterChange} placeholder="Filter" /></th>
                        <th>
                          <Form.Select size="sm" name="admin" value={userFilters.admin} onChange={handleUserFilterChange}>
                            <option value="all">All</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                          </Form.Select>
                        </th>
                        <th>
                          <Form.Select size="sm" name="ads" value={userFilters.ads} onChange={handleUserFilterChange}>
                            <option value="all">All</option>
                            <option value="off">Ad Free</option>
                            <option value="on">Ads On</option>
                          </Form.Select>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((userData) => (
                        <tr key={userData.rowKey || `${userData.user._id}-${userData.team?._id || 'no-team'}`}>
                          <td className="small text-nowrap align-top">
                            <div className="d-flex flex-column gap-1">
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-start text-decoration-none"
                                onClick={() => handleEditClick(userData)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className={`btn btn-link btn-sm p-0 text-start text-decoration-none ${userData.athlete?.isAdmin ? 'text-warning' : 'text-success'}`}
                                disabled={updatingId === userData.user._id || isToggling || userData.user._id === userInfo._id}
                                onClick={() => handleToggleAdmin(userData)}
                              >
                                {updatingId === userData.user._id ? 'Updating...' : (userData.athlete?.isAdmin ? 'Disable admin' : 'Enable admin')}
                              </button>
                              <button
                                type="button"
                                className={`btn btn-link btn-sm p-0 text-start text-decoration-none ${userData.user?.adFree ? 'text-secondary' : 'text-info'}`}
                                disabled={updatingId === userData.user._id || isTogglingAdFree}
                                onClick={() => handleToggleAdFree(userData)}
                              >
                                {updatingId === userData.user._id ? 'Updating...' : (userData.user?.adFree ? 'Enable ads' : 'Disable ads')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-start text-danger text-decoration-none"
                                disabled={!userData.team || isDeletingTeam}
                                onClick={() => handleDeleteTeam(userData)}
                              >
                                Delete team
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-start text-danger text-decoration-none"
                                onClick={() => handleDeleteClick(userData)}
                                disabled={userData.user._id === userInfo._id}
                              >
                                Delete user
                              </button>
                            </div>
                          </td>
                          <td>{formatAdminDate(userData.user?.createdAt)}</td>
                          <td>
                            <div className="small">
                              <div>{userData.user.username}</div>
                              <div className="text-secondary">{userData.user._id}</div>
                            </div>
                          </td>
                          <td>{userData.user.email}</td>
                          <td>
                            {userData.athlete ? (
                              <div className="small">
                                <div>{`${userData.athlete.firstName} ${userData.athlete.lastName}`.trim() || 'Unnamed athlete'}</div>
                                <div className="text-secondary">{userData.athlete._id}</div>
                              </div>
                            ) : 'No athlete'}
                          </td>
                          <td>
                              {userData.team ? (
                              <div className="small">
                              <div>{userData.team.name || 'Unnamed team'}</div>
                              <div className="text-secondary">{userData.team._id}</div>
                              </div>
                            ) : 'No team'}
                          </td>
                            <td>{userData.league?.name || 'No league'}</td>
                          <td>
                            {userData.athlete?.isAdmin
                              ? <span className="badge bg-success">Admin</span>
                              : <span className="badge bg-secondary">User</span>}
                          </td>
                          <td>
                            {userData.user?.adFree
                              ? <span className="badge bg-info text-dark">Ad Free</span>
                              : <span className="badge bg-secondary">Ads On</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-secondary">No users found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title className="text-danger">Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          <p>Are you sure you want to delete this user?</p>
          {userToDelete && (
            <div>
              <p className="mb-1"><strong>Username:</strong> {userToDelete.user.username}</p>
              <p className="mb-1"><strong>Email:</strong> {userToDelete.user.email}</p>
              {userToDelete.athlete && (
                <p className="mb-1"><strong>Name:</strong> {userToDelete.athlete.firstName} {userToDelete.athlete.lastName}</p>
              )}
            </div>
          )}
          <p className="mt-3 text-danger">This action cannot be undone and will delete all associated data.</p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'var(--bg-primary)', borderTop: '1px solid var(--border-light)' }}>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCloseEditModal} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title>Edit User Records</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Username</Form.Label>
                <Form.Control name="username" value={editForm.username} onChange={handleEditFormChange} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control name="email" type="email" value={editForm.email} onChange={handleEditFormChange} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Athlete First Name</Form.Label>
                <Form.Control name="firstName" value={editForm.firstName} onChange={handleEditFormChange} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Athlete Last Name</Form.Label>
                <Form.Control name="lastName" value={editForm.lastName} onChange={handleEditFormChange} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Expected Weekly Distance</Form.Label>
                <Form.Control name="expectedWeeklyDistance" type="number" min="0" step="0.1" value={editForm.expectedWeeklyDistance} onChange={handleEditFormChange} />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Team Name</Form.Label>
                <Form.Control name="teamName" value={editForm.teamName} onChange={handleEditFormChange} disabled={!recordToEdit?.team} />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label>Team Description</Form.Label>
                <Form.Control name="teamDescription" value={editForm.teamDescription} onChange={handleEditFormChange} disabled={!recordToEdit?.team} />
              </Form.Group>
            </div>
          </div>
          {!recordToEdit?.team && (
            <p className="small text-secondary mt-3 mb-0">This user does not currently have a team record to edit.</p>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'var(--bg-primary)', borderTop: '1px solid var(--border-light)' }}>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={isSavingRecord}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRecord} disabled={isSavingRecord}>
            {isSavingRecord ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(leagueToReset)} onHide={handleCloseResetDraftModal} centered>
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title className="text-danger">Reset League Draft</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)' }}>
          <p className="mb-2">
            This will reset the current draft cycle for <strong>{leagueToReset?.name}</strong>.
          </p>
          <p className="mb-2">
            All athletes will be removed from league team rosters, team draft totals will be cleared, and the current league draft will be deleted.
          </p>
          <p className="mb-0 text-danger">
            League admins will then need to schedule a new draft date and run the draft again.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'var(--bg-primary)', borderTop: '1px solid var(--border-light)' }}>
          <Button variant="secondary" onClick={handleCloseResetDraftModal} disabled={isResettingLeagueDraft}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmResetDraft} disabled={isResettingLeagueDraft}>
            {isResettingLeagueDraft ? 'Resetting...' : 'Reset Draft'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminScreen;
